import {
  Box,
  ButtonBase,
  InputAdornment,
  TextField,
} from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { useEffect, useRef, useState } from "react";
import { AppColors } from "resources/AppColors";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import { validFileSize } from "utils/HelperFunctions";

const FileInput = ({
  existingFile,
  fileType,
  label,
  onChange,
  reset,
  error,
}) => {
  const ref = useRef();
  const classes = useStyles();
  const theme = useTheme();
  const [file, setFile] = useState(existingFile ? existingFile : null);
  const [fileDataURL, setFileDataURL] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (event) => {
    const files = Array.from(event.target.files);
    const [file] = files;

    setErrorMessage("");

    if (!validFileSize(file)) {
      setErrorMessage("File is too big");
      return;
    }

    setFile(file);

    if (!!onChange) onChange({ target: { value: file } });
  };

  useEffect(() => {
    let fileReader,
      isCancel = false;
    if (file && typeof file !== "string") {
      fileReader = new FileReader();
      fileReader.onload = (e) => {
        const { result } = e.target;
        if (result && !isCancel) {
          setFileDataURL(result);
        }
      };
      fileReader.readAsDataURL(file);
    }
    return () => {
      isCancel = true;
      if (fileReader && fileReader.readyState === 1) {
        fileReader.abort();
      }
    };
  }, [file]);

  useEffect(() => {
    if (!!existingFile) setFile(existingFile);
    else setFile(null);
  }, [reset]);

  return (
    <Box
      position='relative'
      height={98}
      color={
        !!errorMessage
          ? theme.palette.error.main
          : theme.palette.background.paper
      }
    >
      <Box position='absolute' top={0} bottom={0} left={0} right={0}>
        <TextField
          variant='outlined'
          className={classes.textFieldLabel}
          margin='normal'
          fullWidth
          label={label}
          value={file ? file?.name || "Stored Image" : ""}
          error={!!errorMessage}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                {file ? <CloudDoneIcon /> : <CloudUploadIcon />}
                {file && (
                  <>
                    {typeof file === "string" && file.startsWith("data:") ? (
                      <Box
                        component='img'
                        src={`data:${fileType};base64,${file}`}
                        alt='Stored image'
                        sx={{ width: 25, height: 25, pl: 1 }}
                      />
                    ) : (
                      typeof file === "string" && file.startsWith("http") ? (
                        <Box
                          component='img'
                          src={file}
                          alt='Stored image'
                          sx={{ width: 25, height: 25, pl: 1 }}
                        />
                      ) : (
                        <Box
                          component='img'
                          src={fileDataURL}
                          alt='Stored image'
                          sx={{ width: 25, height: 25, pl: 1 }}
                        />
                      )
                    )}
                  </>
                )}
              </InputAdornment>
            ),
          }}
          helperText={errorMessage}
        />
      </Box>
      <ButtonBase
        className={classes.button}
        component='label'
        onKeyDown={(e) => e.key === " " && ref.current?.click()}
        data-cy='file-input'
      >
        <input
          ref={ref}
          type='file'
          accept='image/*'
          hidden
          onChange={handleChange}
        />
      </ButtonBase>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: AppColors.PRIMARY,
      },
    },
    "& .MuiInputBase-root": {
      color: AppColors.PRIMARY,
    },
    "& .MuiInputLabel-root": {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
  button: {
    width: "100%",
    height: "100%",
  },
}));

export default FileInput;
